import React, { useState } from 'react';
import { setSetting } from '../services/api';
import { FiSave, FiKey, FiDatabase } from 'react-icons/fi';
import '../styles/SettingsPage.css';

function SettingsPage() {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  const [formData, setFormData] = useState({
    bitbucket_ssh_key: '',
    github_ssh_key: '',
    ssh_private_key: '',
    aws_access_key: '',
    aws_secret_key: '',
    rustic_repo_password: '',
    aws_region: 'ap-south-1',
    s3_bucket_name: '',
    job_history_limit: '10'
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Save each setting
      const settings = [
        { key: 'bitbucket_ssh_key', value: formData.bitbucket_ssh_key, encrypted: true },
        { key: 'github_ssh_key', value: formData.github_ssh_key, encrypted: true },
        { key: 'ssh_private_key', value: formData.ssh_private_key, encrypted: true },
        { key: 'aws_access_key', value: formData.aws_access_key, encrypted: true },
        { key: 'aws_secret_key', value: formData.aws_secret_key, encrypted: true },
        { key: 'aws_region', value: formData.aws_region, encrypted: false },
        { key: 's3_bucket_name', value: formData.s3_bucket_name, encrypted: false },
        { key: 'rustic_repo_password', value: formData.rustic_repo_password, encrypted: true },
        { key: 'job_history_limit', value: formData.job_history_limit, encrypted: false }
      ];

      for (const setting of settings) {
        if (setting.value) {
          await setSetting(setting.key, setting.value, setting.encrypted);
        }
      }

      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h2>Settings</h2>
      </div>

      <form onSubmit={handleSave} className="settings-form">
        <section className="settings-section">
          <h3><FiKey /> SSH Keys</h3>
          <p className="section-description">
            Paste your SSH private keys for Git repositories and server access.
            Keys are stored encrypted.
          </p>

          <div className="form-group">
            <label>Bitbucket SSH Private Key</label>
            <textarea
              value={formData.bitbucket_ssh_key}
              onChange={(e) => setFormData({ ...formData, bitbucket_ssh_key: e.target.value })}
              placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
              rows={6}
            />
          </div>

          <div className="form-group">
            <label>GitHub SSH Private Key</label>
            <textarea
              value={formData.github_ssh_key}
              onChange={(e) => setFormData({ ...formData, github_ssh_key: e.target.value })}
              placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
              rows={6}
            />
          </div>

          <div className="form-group">
            <label>Server SSH Private Key</label>
            <textarea
              value={formData.ssh_private_key}
              onChange={(e) => setFormData({ ...formData, ssh_private_key: e.target.value })}
              placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
              rows={6}
            />
            <small>Used for connecting to servers for backup and deployment</small>
          </div>
        </section>

        <section className="settings-section">
          <h3><FiDatabase /> AWS S3 Configuration</h3>
          <p className="section-description">
            Configure AWS credentials for backup storage.
          </p>

          <div className="form-group">
            <label>AWS Access Key</label>
            <input
              type="text"
              value={formData.aws_access_key}
              onChange={(e) => setFormData({ ...formData, aws_access_key: e.target.value })}
              placeholder="AKIAIOSFODNN7EXAMPLE"
            />
          </div>

          <div className="form-group">
            <label>AWS Secret Key</label>
            <input
              type="password"
              value={formData.aws_secret_key}
              onChange={(e) => setFormData({ ...formData, aws_secret_key: e.target.value })}
              placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
            />
          </div>

          <div className="form-group">
            <label>AWS Region</label>
            <select
              value={formData.aws_region}
              onChange={(e) => setFormData({ ...formData, aws_region: e.target.value })}
            >
              <option value="us-east-1">US East (N. Virginia)</option>
              <option value="us-east-2">US East (Ohio)</option>
              <option value="us-west-1">US West (N. California)</option>
              <option value="us-west-2">US West (Oregon)</option>
              <option value="ap-south-1">Asia Pacific (Mumbai)</option>
              <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
              <option value="eu-west-1">Europe (Ireland)</option>
            </select>
          </div>

          <div className="form-group">
            <label>S3 Bucket Name</label>
            <input
              type="text"
              value={formData.s3_bucket_name}
              onChange={(e) => setFormData({ ...formData, s3_bucket_name: e.target.value })}
              placeholder="backup-deploy-bucket"
            />
          </div>
          <div className="form-group">
            <label>Rustic Repo Password</label>
            <input
              type="password"
              value={formData.rustic_repo_password}
              onChange={(e) => setFormData({ ...formData, rustic_repo_password: e.target.value })}
              placeholder="wJalrXUt"
            />
          </div>
        </section>

        <section className="settings-section">
          <h3>Job History</h3>
          <div className="form-group">
            <label>Keep Last N Jobs</label>
            <input
              type="number"
              min="1"
              max="100"
              value={formData.job_history_limit}
              onChange={(e) => setFormData({ ...formData, job_history_limit: e.target.value })}
            />
            <small>Older jobs will be automatically deleted</small>
          </div>
        </section>

        {message && (
          <div className={`settings-message ${message.includes('success') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <div className="settings-actions">
          <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
            <FiSave /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SettingsPage;
